import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  IndianRupee, TrendingUp, Receipt, Users, Package, Banknote,
  AlertCircle, Activity, Wallet, Sparkles, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from "recharts";
import { motion } from "framer-motion";

const KPIS = [
  { key: "today_sales", label: "Today's Sales", icon: TrendingUp, format: inr, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400" },
  { key: "monthly_sales", label: "Monthly Sales", icon: IndianRupee, format: inr, color: "text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400" },
  { key: "total_revenue", label: "Total Revenue", icon: Wallet, format: inr, color: "text-violet-600 bg-violet-50 dark:bg-violet-500/10 dark:text-violet-400" },
  { key: "outstanding", label: "Outstanding", icon: AlertCircle, format: inr, color: "text-orange-600 bg-orange-50 dark:bg-orange-500/10 dark:text-orange-400" },
  { key: "total_customers", label: "Customers", icon: Users, format: (v) => v, color: "text-pink-600 bg-pink-50 dark:bg-pink-500/10 dark:text-pink-400" },
  { key: "total_products", label: "Products", icon: Package, format: (v) => v, color: "text-cyan-600 bg-cyan-50 dark:bg-cyan-500/10 dark:text-cyan-400" },
  { key: "total_expenses", label: "Expenses", icon: Banknote, format: inr, color: "text-rose-600 bg-rose-50 dark:bg-rose-500/10 dark:text-rose-400" },
  { key: "profit", label: "Profit", icon: TrendingUp, format: inr, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
    // Fetch once on mount; api/setData are stable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;
  }
  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Live snapshot of your business performance</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="secondary" className="gap-1.5"><Sparkles size={12} /> AI insights ready</Badge>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {KPIS.map((kpi, i) => (
          <motion.div
            key={kpi.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="kpi-card"
            data-testid={`kpi-${kpi.key}`}
          >
            <div className={`w-10 h-10 rounded-lg ${kpi.color} flex items-center justify-center mb-3`}>
              <kpi.icon size={18} />
            </div>
            <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">{kpi.label}</div>
            <div className="font-heading text-2xl font-bold tracking-tight mt-1">{kpi.format(k[kpi.key])}</div>
          </motion.div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-bold text-lg">Sales & Expenses</div>
              <div className="text-xs text-muted-foreground">Last 7 days</div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.sales_chart}>
                <defs>
                  <linearGradient id="sales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-3))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => inr(v)}
                />
                <Area type="monotone" dataKey="sales" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#sales)" />
                <Area type="monotone" dataKey="expense" stroke="hsl(var(--chart-3))" strokeWidth={2} fill="url(#exp)" />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-lg mb-4">Top Customers</div>
          {data.top_customers.length === 0 && <div className="text-sm text-muted-foreground">No data yet</div>}
          <div className="space-y-3">
            {data.top_customers.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3" data-testid={`top-customer-${i}`}>
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                </div>
                <div className="font-semibold text-sm">{inr(c.total)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Activity + Top products + Low stock */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <div className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><Activity size={18} /> Recent Activity</div>
          <div className="space-y-2">
            {data.recent_activity.length === 0 && <div className="text-sm text-muted-foreground">No recent activity</div>}
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.amount >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"}`}>
                  {a.amount >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.date?.slice(0, 10)}</div>
                </div>
                <div className={`font-semibold text-sm ${a.amount >= 0 ? "" : "text-rose-600"}`}>{inr(Math.abs(a.amount))}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-lg mb-4">Top Products</div>
          <div className="space-y-3">
            {data.top_products.length === 0 && <div className="text-sm text-muted-foreground">No sales yet</div>}
            {data.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className="text-xs font-bold text-muted-foreground w-5">{i + 1}</div>
                <div className="flex-1 min-w-0 font-medium truncate text-sm">{p.name}</div>
                <Badge variant="secondary">{p.qty} sold</Badge>
              </div>
            ))}
          </div>
          {data.low_stock?.length > 0 && (
            <>
              <div className="font-heading font-bold text-lg mt-6 mb-3 flex items-center gap-2 text-orange-600">
                <AlertCircle size={18} /> Low Stock
              </div>
              <div className="space-y-2">
                {data.low_stock.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="truncate font-medium">{p.name}</span>
                    <Badge variant="destructive">{p.current_stock} left</Badge>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
